import { forwardRef } from 'react'
import type { InputHTMLAttributes } from 'react'
import { DateField } from './DateField'

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
  hint?: string
}

/**
 * Generic labeled input that forwards the ref so react-hook-form can register it.
 *
 * Behavior special pentru type="date":
 *   - Foloseste componenta DateField (input dd/mm/yyyy + calendar popover RO).
 *   - value/onChange folosesc tot ISO yyyy-mm-dd intern (compatibil cu HTML5 date).
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(props, ref) {
  const { label, error, hint, id, className, type, value, onChange, disabled, ...rest } = props

  // Pentru date inputs — fallback la DateField (calendar custom RO)
  if (type === 'date' && !disabled) {
    return (
      <div className={className}>
        <DateField
          label={label}
          value={typeof value === 'string' ? value : ''}
          onChange={(iso) => {
            // Simulam evenimentul native pentru compatibilitate cu pages existente
            if (onChange) {
              const ev = {
                target: { value: iso },
                currentTarget: { value: iso },
              } as React.ChangeEvent<HTMLInputElement>
              onChange(ev)
            }
          }}
          hint={hint && !error ? hint : undefined}
        />
        {error && <p className="mt-1 text-xs text-b4l-red">{error}</p>}
      </div>
    )
  }

  const inputId = id ?? `field-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <div className={className}>
      <label htmlFor={inputId} className="mb-1 block text-xs font-medium text-b4l-text-muted">
        {label}
      </label>
      <input
        id={inputId}
        ref={ref}
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...rest}
        className="w-full rounded border border-b4l-border px-3 py-2 outline-none focus:border-b4l-green disabled:bg-b4l-bg disabled:text-b4l-text-muted"
      />
      {hint && !error && <p className="mt-1 text-[11px] text-b4l-text-dim">{hint}</p>}
      {error && <p className="mt-1 text-xs text-b4l-red">{error}</p>}
    </div>
  )
})
